import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { PaginationAPI, theme } from '../../../../utils/TableStyles';
import { usePopup } from '../../../../context/PopupContext';
import { useUser } from '../context/UserContext';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { ErrorUI } from '../../../../utils/ErrorHandler';
import API from '../../../../api';
import { entityTypeLabel } from '../../../../utils/EntityTypeLabel';

export default function EntitiesTab({ userID }) {
  const { isLoading, errorMessage } = useUser();
  const { showPopup, setPopUpText } = usePopup();

  const [entities, setEntities] = useState([]);
  const [isEntitiesApiLoading, setIsEntitiesApiLoading] = useState(true);
  const [apiResponseData, setApiResponseData] = useState({});
  const [authError, setAuthError] = useState('');

  /* ------------------------------- Pagination ------------------------------- */

  const [page, setPage] = useState(0);
  const entitiesRowsPerPage = 10;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /* ------------------------------ Entity Handling ----------------------------- */

  useEffect(() => {
    getEntitiesList();
  }, []);

  const getEntitiesList = () => {
    setAuthError('');
    setIsEntitiesApiLoading(true);
    API.get(`userentity/user/${userID}`)
      .then((response) => {
        setApiResponseData(response.data);
        setEntities(response.data.data);
      })
      .catch((error) => {
        if (error?.response && error.response?.status === 403) {
          setAuthError(error?.response?.data?.message || 'Utente non autorizzato');
        } else {
          setPopUpText("Errore nel caricamento delle entità associate all'utente.");
          showPopup(false);
        }
      })
      .finally(() => setIsEntitiesApiLoading(false));
  };

  useEffect(() => {
    if (Object.keys(apiResponseData).length > 0) {
      setEntities(apiResponseData.data);
      setPage(apiResponseData.current_page - 1);
    }
  }, [apiResponseData]);

  return (
    <>
      {isLoading ? (
        LoadingUI()
      ) : authError.length > 0 ? (
        ErrorUI(authError)
      ) : errorMessage.length === 0 ? (
        <>
          <Grid container spacing={3} sx={{ marginTop: '24px' }}>
            <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
              <Card sx={{ position: 'relative' }}>
                <TableContainer sx={{ padding: '24px 24px 6px' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo Entità</TableCell>
                        <TableCell>Entità</TableCell>
                      </TableRow>
                    </TableHead>
                    {isEntitiesApiLoading ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ textAlign: 'center' }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : entities?.length > 0 ? (
                      <TableBody>
                        {entities.map((entity) => (
                          <TableRow key={entity.id}>
                            <TableCell>{entityTypeLabel(entity.entity, true)}</TableCell>

                            <TableCell>{entity.entity.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2">Nessuna entità disponibile</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                    <TableFooter>
                      <TableRow>
                        <ThemeProvider theme={theme}>
                          <TablePagination
                            sx={{ borderBottom: 'none' }}
                            rowsPerPageOptions={[]}
                            colSpan={6}
                            count={apiResponseData?.total || 0}
                            rowsPerPage={entitiesRowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            labelRowsPerPage="Righe per Pagina"
                            ActionsComponent={(props) => (
                              <PaginationAPI
                                {...props}
                                elements={10}
                                apiResponseData={apiResponseData}
                                setApiResponseData={setApiResponseData}
                                lastPage={apiResponseData?.last_page}
                              />
                            )}
                          />
                        </ThemeProvider>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </Grid>
        </>
      ) : (
        ErrorUI(errorMessage)
      )}
    </>
  );
}
